import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslate } from '@/translations';
import { useUploadDocument, useDocumentTypes } from '@/api/DocumentManagement';
import { UploadDocument, AppEntities } from '@/types';

import { useSearchParams } from 'react-router-dom';

import { App, Col, Form, Input, Modal, Row, Select, Spin } from 'antd';
import { Title } from './UploadDocumentModal.styled';
import { UploadDocumentModalProps } from './types';

const fileReader = new FileReader();

export const UploadDocumentModal: FC<UploadDocumentModalProps> = memo(
  ({ isOpen, closeModal, initialValues }) => {
    const { message } = App.useApp();
    const { isFetching, data } = useDocumentTypes();
    const { mutateAsync: uploadDocument } = useUploadDocument();
    const { t } = useTranslate();
    const [, setSearchParams] = useSearchParams();

    const [fileName, setFileName] = useState<string>('');
    const [file, setFile] = useState<string>('');
    const [documentTypeCode, setDocumentTypeCode] = useState<string>('');
    const [entity, setEntity] = useState<AppEntities>(
      initialValues?.entity || AppEntities.Vessel,
    );
    const [entityReference, setEntityReference] = useState<string>(
      initialValues?.entityReference || '',
    );

    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (!isFetching && !documentTypeCode) {
        setDocumentTypeCode(data?.[0]?.documentTypeCode || '');
      }
    }, [isFetching, data, documentTypeCode]);

    const handleFileLoad = () => {
      const { result } = fileReader;
      if (typeof result === 'string') {
        setFile(fileReader.result as string);
      }
    };

    useEffect(() => {
      fileReader.addEventListener('load', handleFileLoad, false);

      return () => {
        fileReader.removeEventListener('load', handleFileLoad);
      };
    }, []);

    const handleFileChange = () => {
      const selectedFile = fileInputRef.current?.files?.[0];
      if (selectedFile) {
        setFileName(selectedFile.name || 'document');
        fileReader.readAsDataURL(selectedFile);
      } else {
        setFileName('');
        setFile('');
      }
    };

    const handleSubmit = async () => {
      const requiredFields = {
        fileName,
        documentFileBase64: file,
        documentTypeCode,
        entity,
        entityReference,
      };

      if (Object.values(requiredFields).some((value) => !value)) {
        message.error('All fields are required');
        return;
      }

      const body: UploadDocument = { ...requiredFields };

      const res = await uploadDocument(body);
      if (!res.success) {
        message.error(res.message.Message, 10);
      } else {
        closeModal();
        // clear all URL Search Params
        setSearchParams({}, { replace: true });
      }
    };

    const handleCloseModal = useCallback(() => {
      closeModal();
      // clear all URL Search Params
      setSearchParams({}, { replace: true });
    }, [closeModal, setSearchParams]);

    return (
      <Modal
        open={isOpen}
        onCancel={handleCloseModal}
        okText={t('upload')}
        onOk={handleSubmit}
        footer={(_, { OkBtn }) => <OkBtn />}
        width="50vw"
      >
        <Spin spinning={isFetching}>
          <Title>{t('uploadDocument')}</Title>
          <Row>
            <Col>
              <Form>
                <Form.Item label={t('selectFile')}>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*"
                  />
                </Form.Item>
                <Form.Item label={t('documentTypeCode')} className="mb-3">
                  <Select
                    value={documentTypeCode}
                    onChange={setDocumentTypeCode}
                    options={data?.map((item) => ({
                      label: item.documentTypeCode,
                      value: item.documentTypeCode,
                    }))}
                  />
                </Form.Item>
                <Form.Item label={t('entity')} className="mb-3">
                  <Select
                    value={entity}
                    onChange={(e) => setEntity(e as AppEntities)}
                    options={Object.keys(AppEntities).map((key) => ({
                      label: key,
                      value: key,
                    }))}
                  />
                </Form.Item>
                <Form.Item label={t('entityReference')} className="mb-3">
                  <Input
                    value={entityReference}
                    onChange={(e) => setEntityReference(e.target.value)}
                    placeholder="Vessel name / Transport number / Vehicle VIN"
                  />
                </Form.Item>
              </Form>
            </Col>
          </Row>
        </Spin>
      </Modal>
    );
  },
);
